import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import { Footnote, FootnoteRef, FootNoteRefProps_t } from '../components/FootNote';


export const MPCPage: React.FC<{}> = () => {
    const references: Array<FootNoteRefProps_t> = [
        { id: "Blackmore", ref: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=xZvK9z0AAAAJ&citation_for_view=xZvK9z0AAAAJ:LkGwnXOMwfcC" },
        { id: "Blackmore2", ref: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=xZvK9z0AAAAJ&citation_for_view=xZvK9z0AAAAJ:JV2RwH3_ST0C" },
        { id: "driving", text: "start at 4:53", ref: "https://youtu.be/SvAYJC7jug8?si=NWvsCe2ZVJmSaqLh&t=293" },
        { id: "mini-cheetah", ref: "https://arxiv.org/pdf/1909.06586" },
        { id: "atlas", ref: "https://bostondynamics.com/blog/picking-up-momentum/" },
        { id: "drift", ref: "https://www.youtube.com/watch?v=3x3SqeSdrAE"},
        { id: "race", ref: "https://www.youtube.com/watch?v=joIsgP9StAY"},
        { id: "RL", ref: "https://bostondynamics.com/blog/starting-on-the-right-foot-with-reinforcement-learning/"},
        { id: "prediction", ref: "https://arxiv.org/pdf/2207.05844"},
        { id: "world_model", ref: "https://waymo.com/blog/2026/02/the-waymo-world-model-a-new-frontier-for-autonomous-driving-simulation/"},
        { id: "LQR", ref: "https://en.wikipedia.org/wiki/Linear%E2%80%93quadratic_regulator"},
        { id: "Self-Driving CMU", ref: "https://www.youtube.com/watch?v=4Fh0wIqaynQ&list=PLZnJoM76RM6Jv4f7E7RnzW4rijTUTPI4u&index=22" },
        { id: "Morton", ref: "https://arxiv.org/abs/1805.07472"},
        { id: "Morton2", ref: "https://arxiv.org/abs/1902.09742"},
        { id: "Liu", ref: "https://arxiv.org/pdf/2603.03740"},
    ];

    // This gets the reference number that we render next to the footnote.
    // It just finds the index of the footnote with the given id in the list.
    const f = React.useCallback((id: string) => {
        let index = references.findIndex(ref => ref.id === id);
        if (index == -1) {
            throw Error(`could not find reference: ${id} in list`);
        }
        return index + 1;
    }, [references]);

    return (<main>
        <h1>The Mathematical Beauty of Model Predictive Control</h1>
        <p>
            Model Predictive Control (MPC) is used to land SpaceX's
            rockets<Footnote id={"Blackmore"} f={f} /> <Footnote id={"Blackmore2"} f={f} />,
            control self-driving cars <Footnote id="driving" f={f} />, control Boston Dynamic's walking 
            robots<Footnote id={"mini-cheetah"} f={f} />  <Footnote id={"atlas"} f={f} />, and many more robotic applications. 
        <p>
        <p>
            Researchers at Stanford have used it to drift a DeLorean autonomously<Footnote id="drift" f={f}/> and autonomously race a car<Footnote id="race" f={f}/>.
        </p>
        </p>
            Sometimes people conflate advances in control in those fields with recent advances in AI. While it's 
            true there is interest in using reinforcement learning for some of these applications<Footnote id={"RL"} f={f} /> and 
            AI is used for perception, prediction<Footnote id="prediction" f={f}/> and simulation<Footnote id="world_model" f={f}/>, I think that 
            narrative can overshadow of the cool math in MPC.
        </p>
        <p>
            To be clear I think all of those advances in AI for pereception, prediction and simulation are super cool!
        </p>
        <p>
            I just think MPC is also such a clever idea. When I first learned about in 
            my undergraduate convex optimization class I was blown away. I like to explain it to other mathematically-inclined people
            and blow their mind.
        </p>
        <h3>What is control?</h3>
        <p>
            The basic premise of control is that we have a system and we want to give it inputs (our controls) to 
            make it behave how we like. For example: making a rocket land rather than crash into the ocean. 
        </p>
        <p>
            This very similar to the goal reinforcement learning and other AI techniques try to solve.
        </p>
        <h3>Continuous Time Dynamics</h3>
        <p>
            Let's imagine we have a dynamical system we want to control. That dynamical system can be a robot. 
            One of the classic examples is a double integrator. Our state is our position and its time deriative 
            <BlockMath math="\bold{x} = \begin{bmatrix} x \\ \dot{x} \end{bmatrix}"/> 
            Our control <InlineMath math="u \in \mathbb{R}" /> is the acceleration
            <BlockMath math="\dot{\bold{x}}(t) = \begin{bmatrix} \dot{x} \\ \ddot{x} \end{bmatrix} = \begin{bmatrix} \dot{x} \\ u \end{bmatrix}"/>
            It's called a double integrator because our position is the second integral of our control.
        </p>
        <h3>Discrete Time Dynamics</h3>
        <p>
            We have to put this into a computer which means we will need to discretize it. 
            So from now on we are working with discrete time dynamics with timestep size <InlineMath math="\Delta"/>. I'll use this discretization:
        </p>
            <BlockMath math="\bold{x}_{t+1} = \begin{bmatrix} 1 \ \Delta \\ 0 \ 1 \end{bmatrix} \bold{x}_{t} +  \begin{bmatrix} 0 \\ \Delta \end{bmatrix} \bold{u}_t" />
            <BlockMath math="\bold{x}_{t+1} = A \bold{x}_{t} +  B \bold{u}_t" />
        <p>
            If you didn't understand that, it's ok. From now on all we need to know is that the 
            matrices <InlineMath math="A"/> and <InlineMath math="B"/> describe our dynamics. 
        </p>
        <h3>Goal</h3>
        <p>
            Let's say we have a goal state <InlineMath math="\bold{x}^{\text{goal}}"/> we'd like our system to arrive at. This could be the location of our 
            landing pad along with the correct orientation (up!) and speed (zero!) of the rocket.
        </p>
        <p>
            We can create an optimization problem where we choose a trajectory that arrives as close as it can to that goal
        </p>
            <BlockMath math="\min_{\bold{x}_{1}} \|\bold{x}^{\text{goal}} - \bold{x}_1\|_{2}"/>
        <p>
            But this isn't very helpful because the optimizer is just going to 
            pick <InlineMath math="\bold{x}_{1}=\bold{x}^{\text{goal}}" /> which might not be physically feasible and it doesn't 
            tell us what control input to give to get there. 
        </p>
        <p>
            So intead we constrain our optimization problem such that it obeys our dynamics. Let's assume we are starting at 
            state <InlineMath math="\bold{x}_0" />.
        </p>
        <BlockMath math="\min_{\bold{x}_1, \bold{u}_0} \|\bold{x}^{\text{goal}} - \bold{x}_1\|_{2} \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0"/>
        <p>
            Recall that <InlineMath math="A"/> and <InlineMath math="B"/> are known. They describe the dynamics of our system. 
            And <InlineMath math="\bold{x}_0" /> is known. We just read it from our sensors. And <InlineMath math="\bold{x}^{\text{goal}}"/> is known.
            It's our goal state we picked. 
        </p>
        <p>
            So solving this optimization problem gives us <InlineMath math="\bold{u}_0"/> (the <em>control</em> we should apply now) 
            and <InlineMath math="\bold{x}_1"/> (our <em>prediction</em> of where we will end up). This prediction comes from our dynamics 
            which is sometimes called a <em>model</em>.
        </p>
        <p>
            If you put it all together you get Model Predictive Control. Funny how that worked out so nicely.
        </p>
        <h3>Longer Time Horizons</h3>
        <p>
            But what if our goal cannot be reached in a single timestep? We could try to solve this optimization problem, apply the control,
            and then solve it again. But this locally optimal approach may not be globally optimal. The dynamics may not work well 
            with such a short-sighted approach.
        </p>
        <p>
            So instead we force our optimizer to be aware of the future effects of choosing a control. We choose a time 
            horizon <InlineMath math="T\in \mathbb{N}" />. Let's examine what happens with <InlineMath math="T = 2" />
        </p>
        <BlockMath math="\min_{\bold{x}_1, \bold{x}_2, \bold{u}_0, \bold{u}_1} \|\bold{x}^{\text{goal}} - \bold{x}_2\|_{2} \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ \bold{x}_2 = A\bold{x}_1 + B\bold{u}_1"/>
        <p>
            Solving this gives us a trajectory <InlineMath math="\{\bold{x}_1, \bold{x}_2 \}"/> and the controls that will 
            cause that trajectory <InlineMath math="\{\bold{u}_0, \bold{u}_1 \}"/>.
        </p>
        <p>
            For general <InlineMath math="T"/>
        </p>
        <BlockMath math="\min_{\{\bold{x}_1 ... \bold{x}_T\}, \{\bold{u}_0 ... \bold{u}_{T-1} \}} \|\bold{x}^{\text{goal}} - \bold{x}_T\|_{2} \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ \bold{x}_2 = A\bold{x}_1 + B\bold{u}_1 \\...\\ \bold{x}_T = A\bold{x}_{T-1} + B\bold{u}_{T-1}"/>
        <p>Now we know our trajectory is physically feasible <InlineMath math="T\Delta"/> seconds into the future according to our model.</p>
        <p>When I saw this, it blew it me away.</p>
        <h3>The Imperfections of Real Life</h3>
        <p>
            One approach we could take is to solve that optimization problem and then apply the controls <InlineMath math="\{\bold{u}_0 ... \bold{u}_{T-1} \}"/> it 
            gave us. We should end up at <InlineMath math="\bold{x}_T"/> right?</p>
        <p>
            But we don't. Why? 
        </p>
        <p> Recall that we are discretizing our dynamics. So the discretized dynamics may not actually 
            match the real system. We can choose our timestep size <InlineMath math="\Delta"/> to be smaller 
            to better approximate the true continuous time dynamics. But it's a tradeoeff because our MPC controller 
            looks <InlineMath math="T\Delta"/> seconds ahead. If we decrease <InlineMath math="\Delta"/> then we will 
            look less far ahead. We can increase <InlineMath math="T"/> but that will make our optimization problem larger. So it will
            take longer to solve. It's a tradeoff.
        </p>
        <p>
            Another problem is that our model of the dynamics <InlineMath math="A"/> and <InlineMath math="B"/> might not be 
            completely accurate. This is called "Model-Mismatch". It can happen for all sorts of reasons: sensor noise, our rocket 
            is slightly differently shaped than the rocket we measured to get <InlineMath math="A"/> and <InlineMath math="B"/>, there's
            some wind today, we're approximating our dynamics etc.
        </p>
        <h3>Feedback Control</h3>
        <p>
            There's always going to be a small mismatch between where our model predicts we will end up and where we actually do.
            As we predict further into the future, the more our prediction will diverge from real life. 
        </p>
        <p>
            To solve this, we just solve this optimization problem, apply the first control <InlineMath math="\bold{u}_0"/>, read 
            our new state <InlineMath math="\bold{x}_0"/> from our sensors, and repeat. This is why it's important we can solve the problem quickly.
            Real systems will repeatedly solve this optimization problem at a frequency of anywhere from 20Hz to 200Hz.
        </p>
        <p>
            This concept of applying a control, then reading a sensor measurement, and then applying a new control is called Feedback Control. You can think of it
            as constantly re-adjusting based on feedback from the world. 
        </p>
        <h3>Additional Constraints</h3>
        <p>
            We may have additional constraints we would like to add. Right now the optimizer assumes we can accelerate infinitely. In reality,
            our system may have only limited thrust. We can express that: 
        </p>
        <BlockMath math="\min_{\{\bold{x}_1 ... \bold{x}_T\}, \{\bold{u}_0 ... \bold{u}_{T-1} \}} \|\bold{x}^{\text{goal}} - \bold{x}_T\|_{2} \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ ...\\ \bold{x}_T = A\bold{x}_{T-1} + B\bold{u}_{T-1} \\ \bold{u}_{\min} \leq \bold{u}_0 \leq \bold{u}_{\max} \\ ... \\ \bold{u}_{\min} \leq \bold{u}_{T-1} \leq \bold{u}_{\max} "/>
        <p>
            We may have safety constraints. Imagine if we are driving a car, we might not want to go into another lane or the sidewalk. We could express 
            this as a constraint as well. We might have a speed limit we must obey. We could also use a contraint for that.
        </p>
        <BlockMath math="\min_{\{\bold{x}_1 ... \bold{x}_T\}, \{\bold{u}_0 ... \bold{u}_{T-1} \}} \|\bold{x}^{\text{goal}} - \bold{x}_T\|_{2} \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ ...\\ \bold{x}_T = A\bold{x}_{T-1} + B\bold{u}_{T-1} \\ \bold{u}_{\min} \leq \bold{u}_0 \leq \bold{u}_{\max} \\ ... \\ \bold{u}_{\min} \leq \bold{u}_{T-1} \leq \bold{u}_{\max} \\ \bold{x}_{\min} \leq \bold{x}_1 \leq \bold{x}_{\max} \\ ... \\ \bold{x}_{\min} \leq \bold{x}_{T} \leq \bold{x}_{\max} "/>
        <h3>Variations on the Objective</h3>
        <p>
            For our goal state, we may care about some of the dimensions of our state more than others. Perhaps we don't care what the velocity is 
            as long as we are close to the position of our goal state. We can define a goal matrix <InlineMath math="Q=\begin{bmatrix}1 \ 0 \\ 0 \ 0 \end{bmatrix}"/>
            and change our objective to 
            <BlockMath math="\min_{\{\bold{x}_1 ... \bold{x}_T\}, \{\bold{u}_0 ... \bold{u}_{T-1} \}} (\bold{x}^{\text{goal}} - \bold{x}_T)^T Q (\bold{x}^{\text{goal}} - \bold{x}_T) \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ \bold{x}_2 = A\bold{x}_1 + B\bold{u}_1 \\...\\ \bold{x}_T = A\bold{x}_{T-1} + B\bold{u}_{T-1}"/>
            We could add also add cross terms if we care about those.
        </p>
        <p>
            Maybe rather than a single goal state, we have a whole trajectory we would like to track <InlineMath math="\{\bold{x}^{\text{goal}}_1 \ ...\  \bold{x}^{\text{goal}}_T\}"/>.
            <BlockMath math="\min_{\{\bold{x}_1 ... \bold{x}_T\}, \{\bold{u}_0 ... \bold{u}_{T-1} \}} \sum_{t=1}^{T} (\bold{x}_{t}^{\text{goal}} - \bold{x}_t)^T Q (\bold{x}_{t}^{\text{goal}} - \bold{x}_t) \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ \bold{x}_2 = A\bold{x}_1 + B\bold{u}_1 \\...\\ \bold{x}_T = A\bold{x}_{T-1} + B\bold{u}_{T-1}"/>
        </p>
        <p>
            We might also prefer the controls we pick are smaller. This may be because we think it will make our trajectory smoother. Or perhaps we'd like to use
            less fuel/battery etc. 
            <BlockMath math="\min_{\{\bold{x}_1 ... \bold{x}_T\}, \{\bold{u}_0 ... \bold{u}_{T-1} \}} \sum_{t=1}^{T} (\bold{x}_{t}^{\text{goal}} - \bold{x}_t)^T Q (\bold{x}_{t}^{\text{goal}} - \bold{x}_t) + \bold{u}_{t-1}^TR\bold{u}_{t-1} \\ s.t. \ \bold{x}_1 = A\bold{x}_0 + B\bold{u}_0 \\ \bold{x}_2 = A\bold{x}_1 + B\bold{u}_1 \\...\\ \bold{x}_T = A\bold{x}_{T-1} + B\bold{u}_{T-1}"/>
        </p>
        <h3>Convexity</h3>
        <p>
            If <InlineMath math="Q"/> and <InlineMath math="R"/> are positive semi-definite this problem is convex and can be solve efficiently. In fact 
            a variation of this problem called the Linear Quadratic Regulator (LQR) has a closed-form solution.<Footnote id="LQR" f={f}/> 
        </p>
        <p>
            In this example the dynamics are linear which makes the feasible set convex. But you may have non-linear dynamics 
            that induce a non-convex feasible set. In that case you have a few options: 
            <ol>
                <li>Find some clever relaxation of the problem to a convex one<Footnote id="Blackmore" f={f}/></li>
                <li>
                    Just use some solver that works for non-convex problems. Self-driving cars use 
                    the kinematic bicycle model or dynamic bicycle model which are both non-convex but 
                    they just use nonconvex solvers like IPOPT.<Footnote id="Self-Driving CMU" f={f}/>
                </li>
                <li>
                    Use a neural network to learn a mapping to a higher-dimensional state space where the dynamics are linear.<Footnote id="Morton" f={f}/> <Footnote id="Morton2" f={f}/> <Footnote id="Liu" f={f}/> 
                </li>
            </ol>
        </p>
        <h3>Safety Constraints</h3>
        <p>
            Safety is often defined as staying out of a set of states. But the complement of a convex set is a non-convex set. For example if we have a 
            pedestrian at <InlineMath math="\bold{x}^{\text{ped}}"/> the 
            constraint <InlineMath math="\bold{x}_{t} \in \{\bold{x} :\| \bold{x} - \bold{x}^{\text{ped}} \|_{2} \geq a \}"/> is non-convex. It 
            corresponds to staying out of a sphere of radius <InlineMath math="a"/> centered at <InlineMath math="\bold{x}^{\text{ped}}"/>.
        </p>
        <p>
            One technique is that your high-level path planner creates a 
            goal trajectory <InlineMath math="\{\bold{x}^{\text{goal}}_1 \ ...\  \bold{x}^{\text{goal}}_T\}"/> that stays at least distance <InlineMath math="a"/> away from 
            any obstacle. Then you constrain your trajectory to track the trajectory within <InlineMath math="a"/> like <InlineMath math="\|\bold{x}_1 - \bold{x}^{\text{goal}}_1\|_2 \leq a"/>.
        </p>
        <p>
            That technique relies on you having a planner that can create such a trajectory.
        </p>


      <section id="footnotes">
        {references.map((item, i) => (
          <FootnoteRef id={item.id} text={item.text} ref={item.ref} number={i+1} />
          ))
        }
      </section>
    </main>
  )
};
